import { createFileRoute } from "@tanstack/react-router";
import type { ServiceBundle, ServiceBundleItemWithService } from "@somasi/shared";
import { Fragment, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import type { ServiceBundleItemDraft } from "@/integrations/aws/resources";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ServiceSelect,
  invalidateServiceBundleLists,
  type ServiceRow,
} from "@/components/entity-select";
import { SearchableSelect, type SelectOption } from "@/components/searchable-select";
import { listServiceSchedules, money } from "@/lib/billing";
import { Truncated } from "@/components/truncated";
import { Boxes, ChevronRight, Pencil, Plus, Trash2, TriangleAlert } from "lucide-react";
import { notify } from "@/lib/notify";
import { DataExportMenu } from "@/components/export-menu";
import { ListExportDialog, type ExportScope } from "@/components/list-export-dialog";
import {
  BUNDLE_SERVICE_COLUMNS,
  BUNDLE_SUMMARY_COLUMNS,
  bundleItemTerms,
  bundleServiceRows,
  bundleSummaryRows,
  pdfColumns,
} from "@/lib/service-export";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/service-bundles")({
  head: () => ({
    meta: [
      { title: "Service Bundles — Somasi Ops" },
      {
        name: "description",
        content:
          "Named sets of services that are delivered together, so a log entry or an invoice takes them in one pick.",
      },
      { property: "og:title", content: "Service Bundles — Somasi Ops" },
      {
        property: "og:description",
        content:
          "Named sets of services that are delivered together, so a log entry or an invoice takes them in one pick.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ServiceBundlesPage,
});

type BundleWithItems = ServiceBundle & { items?: ServiceBundleItemWithService[] };

/** One page of the list, as the query holds it. */
interface BundlesPage {
  rows: BundleWithItems[];
  total: number;
}

function ServiceBundlesPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceBundle | null>(null);
  const [search, setSearch] = useState("");
  // "all" rather than "active": this is the maintenance screen, so a bundle
  // somebody marked inactive has to be findable here to be brought back.
  const [activeFilter, setActiveFilter] = useState("all");
  const sort = useListSort({ column: "name", ascending: true });
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  const filters = { query, activeFilter, sort: sort.sort };
  // Five data columns, plus the checkbox column for an operator who can bulk-edit.
  const columnCount = canEdit ? 6 : 5;
  /** The bundle whose services are showing under its row; one at a time. */
  const [expanded, setExpanded] = useState<string | null>(null);
  const toggleExpanded = (id: string) => setExpanded((current) => (current === id ? null : id));
  const pager = usePagination({ resetKey: filters });
  const queryKey = ["service_bundles", { ...filters, page: pager.page, pageSize: pager.pageSize }];

  /** The list the filters describe. Filtering and sorting happen in the API. */
  const filteredList = () => {
    // `include=items` so the Services column counts them without a request per
    // row — the same one-read-and-group the contacts list does for roles.
    const list = api.serviceBundles.listWithItems();

    if (activeFilter !== "all") list.eq("active", activeFilter === "active");
    if (query) {
      list.or([
        { column: "code", op: "ilike", value: `%${query}%` },
        { column: "name", op: "ilike", value: `%${query}%` },
        { column: "description", op: "ilike", value: `%${query}%` },
      ]);
    }

    list.order(sort.sort.column, { ascending: sort.sort.ascending });
    // Only `code` is unique, so without a tie-break two bundles sharing a
    // value would swap places between requests.
    if (sort.sort.column !== "name") list.order("name");

    return list;
  };

  const { data: result, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<BundlesPage> => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as BundleWithItems[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;
  const sel = useRowSelection(data);

  const refresh = () => invalidateServiceBundleLists(qc);

  /** Shows the change at once; the refetch that follows is the authority. */
  const patchCache = (rows: BundleWithItems[]) =>
    qc.setQueryData<BundlesPage>(queryKey, (previous) => previous && { ...previous, rows });

  /**
   * Deletes the bundle outright, items and all.
   *
   * Safe in a way deleting a service is not: nothing stores a bundle id once it
   * has expanded into lines, so no log entry and no invoice is left pointing at
   * a row that is gone. The warning is only about the operators who were picking
   * it, which is why it names how many services go with it.
   */
  const remove = async (row: BundleWithItems) => {
    const count = row.items?.length ?? 0;
    const what =
      count > 0
        ? `bundle “${row.name}” and its ${count} service${count === 1 ? "" : "s"}`
        : `bundle “${row.name}”`;
    if (!(await confirmDelete(what))) return;

    const { error } = await api.serviceBundles.remove(row.id);
    if (error) return notify.error(error);
    notify.success("Bundle deleted — entries already logged from it are untouched");
    refresh();
  };

  /**
   * The switch in the Active column: one `PATCH` of one column.
   *
   * Marking inactive takes a bundle out of the pickers on the log and invoice screens
   * while leaving it here to be edited or brought back. Nothing already logged
   * from it changes, because nothing logged from it refers to it.
   */
  const setActive = async (row: BundleWithItems, active: boolean) => {
    patchCache(data.map((bundle) => (bundle.id === row.id ? { ...bundle, active } : bundle)));

    const { error } = await api.serviceBundles.update(row.id, { active });
    // The refetch below puts the switch back where it was if the write failed.
    if (error) notify.error(error);
    else notify.success(`“${row.name}” is now ${active ? "active" : "inactive"}`);
    refresh();
  };

  /** Every bundle, filters aside — the count the export offers as "entire". */
  const { data: everyBundle } = useQuery({
    queryKey: ["service_bundles", "total"],
    queryFn: async () => {
      const { count, error } = await api.serviceBundles.list().countOnly();
      if (error) throw error;
      return count ?? 0;
    },
  });

  /** Which report's export dialog is open. */
  const [exportReport, setExportReport] = useState<"summary" | "services" | null>(null);

  /**
   * The bundles a scope describes, items attached, in the list's own order.
   *
   * The whole set rather than the page on screen. "Entire" is the same read
   * with the filters left off, so both scopes walk the one query the table uses.
   */
  const exportBundles = async (scope: ExportScope) => {
    const list = scope === "filtered" ? filteredList() : api.serviceBundles.listWithItems();
    if (scope === "all") list.order("name");
    const { data, error, truncated } = await list.collect();
    if (error) throw error;
    return { bundles: (data ?? []) as BundleWithItems[], truncated };
  };

  const filterSummary =
    [query ? `search “${query}”` : null, activeFilter === "all" ? null : activeFilter]
      .filter(Boolean)
      .join(", ") || null;

  const exportGroups = [
    {
      items: [
        {
          label: "Bundle summary",
          hint: "One row per bundle: services in it, status and bundle total. Excel or PDF.",
          run: () => setExportReport("summary"),
        },
        {
          label: "Services by bundle",
          hint: "One row per service in each bundle: description, quantity, rate and amount. Excel or PDF.",
          run: () => setExportReport("services"),
        },
      ],
    },
  ];

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Service Bundles"
          description="Services that go out together, added in one pick to the work log, an invoice or a service plan."
          actions={
            <>
              <DataExportMenu groups={exportGroups} />
              {canEdit && (
                <Button
                  onClick={() => {
                    setEditing(null);
                    setOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> New bundle
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search bundle name, code or description…"
          />
          <FilterSelect
            value={activeFilter}
            onChange={setActiveFilter}
            className="w-36"
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.serviceBundles}
            entityLabel="bundle"
            activeColumn="active"
            onDone={refresh}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="code" className="w-32">
                Code
              </SortHeader>
              <SortHeader sort={sort} column="name">
                Bundle
              </SortHeader>
              <TableHead className="w-32">Services</TableHead>
              <SortHeader sort={sort} column="active" className="w-28">
                Active
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">
                {canEdit && "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : data.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                <Boxes className="mx-auto mb-2 h-6 w-6 opacity-50" />
                {query || activeFilter !== "all"
                  ? "No bundles match these filters."
                  : "No bundles yet."}
              </TableMessageRow>
            ) : (
              data.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={sel.isSelected(row.id) ? "selected" : undefined}
                    aria-expanded={expanded === row.id}
                    className="cursor-pointer"
                    onClick={(e) => {
                      // The row's own controls keep their meaning; anywhere else
                      // on the row opens or closes its list of services.
                      if ((e.target as HTMLElement).closest("button, a, input, label")) return;
                      toggleExpanded(row.id);
                    }}
                  >
                    {canEdit && (
                      <TableCell>
                        <RowCheckbox sel={sel} id={row.id} />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.code}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <Truncated text={row.name} className="font-medium" />
                      {row.description && (
                        <Truncated
                          text={row.description}
                          className="text-xs text-muted-foreground"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(row.id)}
                        aria-label={`${expanded === row.id ? "Hide" : "Show"} the services in ${row.name}`}
                        className="inline-flex items-center gap-1 rounded text-sm text-muted-foreground hover:text-foreground"
                      >
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 transition-transform",
                            expanded === row.id && "rotate-90",
                          )}
                        />
                        {row.items?.length ?? 0} service{row.items?.length === 1 ? "" : "s"}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={row.active}
                          disabled={!canEdit}
                          aria-label={`Mark ${row.active ? "inactive" : "active"}: ${row.name}`}
                          onCheckedChange={(value) => setActive(row, value)}
                        />
                        <span className="text-xs text-muted-foreground">
                          {row.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <RowActions>
                          <RowAction
                            label="Edit"
                            onClick={() => {
                              setEditing(row);
                              setOpen(true);
                            }}
                          >
                            <Pencil />
                          </RowAction>
                          <RowAction label="Delete" destructive onClick={() => remove(row)}>
                            <Trash2 />
                          </RowAction>
                        </RowActions>
                      )}
                    </TableCell>
                  </TableRow>
                  {expanded === row.id && (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={columnCount} className="px-6 py-3">
                        <BundleContents items={row.items} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="bundle" />
      </ListPageTable>

      <BundleDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={refresh} />

      {/* Mounted on opening, so the dialog reads the filters as they are now. */}
      {exportReport === "summary" && (
        <ListExportDialog
          open
          onOpenChange={(value) => !value && setExportReport(null)}
          title="Export bundle summary"
          description="One row per bundle, with how many services it holds, whether it is active, and what it comes to — each service at the bundle's rate, or its standard fee where the bundle sets none."
          filenameBase="somasi-bundle-summary"
          reportTitle="Bundle summary"
          unitPlural="bundles"
          entireLabel="Every bundle"
          entireHint="Active and inactive, ignoring the search and filter on the list."
          filteredCount={total}
          totalCount={everyBundle}
          filterSummary={filterSummary}
          columns={BUNDLE_SUMMARY_COLUMNS}
          pdfCols={pdfColumns([
            "Code",
            "Bundle",
            "Description",
            "Services",
            "Total quantity",
            "Status",
            "Bundle total",
          ])}
          pdfRow={(row) => ({ ...row, "Bundle total": money(Number(row["Bundle total"])) })}
          fetchRows={async (scope) => {
            const { bundles, truncated } = await exportBundles(scope);
            return { rows: bundleSummaryRows(bundles), truncated };
          }}
        />
      )}

      {exportReport === "services" && (
        <ListExportDialog
          open
          onOpenChange={(value) => !value && setExportReport(null)}
          title="Export services by bundle"
          description="One row per service in each bundle, grouped by bundle: what the invoice prints for it, the quantity, the rate and the amount. A service in several bundles has a row in each."
          filenameBase="somasi-bundle-services"
          reportTitle="Services by bundle"
          unitPlural="bundles"
          entireLabel="Every bundle"
          entireHint="Active and inactive, ignoring the search and filter on the list."
          filteredCount={total}
          totalCount={everyBundle}
          filterSummary={filterSummary}
          columns={BUNDLE_SERVICE_COLUMNS}
          pdfCols={pdfColumns([
            "Service code",
            "Service",
            "Bundle code",
            "Bundle",
            "Invoice description",
            "Quantity",
            "Rate",
            "Rate from",
            "Amount",
          ])}
          pdfRow={(row) => ({
            ...row,
            Rate: row.Rate === "" ? "—" : money(Number(row.Rate)),
            Amount: row.Amount === "" ? "—" : money(Number(row.Amount)),
          })}
          fetchRows={async (scope) => {
            const { bundles, truncated } = await exportBundles(scope);
            const rows = bundleServiceRows(bundles);
            return {
              rows,
              truncated,
              // The choice was offered in bundles; the file is in services.
              summary: `${rows.length.toLocaleString()} service line${rows.length === 1 ? "" : "s"} from ${bundles.length.toLocaleString()} bundle${bundles.length === 1 ? "" : "s"}`,
            };
          }}
        />
      )}
    </ListPage>
  );
}

/**
 * What is in the bundle, as the row shows it when opened: one line per service,
 * with what the invoice prints for it, its quantity, rate and amount, and whether
 * it has since been marked inactive.
 */
function BundleContents({ items }: { items?: ServiceBundleItemWithService[] }) {
  if (!items?.length) {
    return <span className="text-xs text-muted-foreground">No services in this bundle yet.</span>;
  }

  const lines = items.map((item) => ({ item, ...bundleItemTerms(item) }));
  const total = lines.reduce((sum, line) => sum + (line.amount ?? 0), 0);
  const unpriced = lines.filter((line) => line.amount == null).length;

  return (
    <table className="w-full max-w-5xl text-sm">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="w-28 pb-1.5 font-medium">Code</th>
          <th className="pb-1.5 font-medium">Service</th>
          <th className="pb-1.5 font-medium">Invoice description</th>
          <th className="w-12 pb-1.5 text-right font-medium">Qty</th>
          <th className="w-32 pb-1.5 text-right font-medium">Rate</th>
          <th className="w-32 pb-1.5 text-right font-medium">Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {lines.map(({ item, description, describedByBundle, rate, rateFromBundle, amount }) => (
          <tr key={item.id} className="align-top">
            <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">
              {item.service_code ?? "—"}
            </td>
            <td className="py-1.5 pr-3">
              {item.service_name ?? "Unknown service"}
              {!item.service_active && (
                <Badge variant="destructive" className="ml-2">
                  Inactive
                </Badge>
              )}
            </td>
            <td className="py-1.5 pr-3">
              {description ?? "—"}
              {!describedByBundle && description && (
                <span className="block text-[11px] text-muted-foreground">
                  Service&apos;s own wording
                </span>
              )}
            </td>
            <td className="py-1.5 text-right tabular-nums">{item.quantity}</td>
            <td className="py-1.5 text-right tabular-nums">
              {rate == null ? "—" : money(Number(rate))}
              <span className="block text-[11px] text-muted-foreground">
                {rateFromBundle ? "Bundle rate" : rate == null ? "No rate set" : "Standard fee"}
              </span>
            </td>
            <td className="py-1.5 text-right font-medium tabular-nums">
              {amount == null ? "—" : money(amount)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t">
          <td colSpan={5} className="pt-2 text-right text-xs text-muted-foreground">
            Bundle total
            {unpriced > 0 && ` — ${unpriced} service${unpriced === 1 ? "" : "s"} unpriced`}
          </td>
          <td className="pt-2 text-right font-semibold tabular-nums">{money(total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

/** One service line in the editor, before it is saved. */
interface ItemDraft {
  /** Stable across re-renders; `id` is the stored row, absent on a new line. */
  key: string;
  id?: string;
  service_id: string;
  quantity: string;
  /** What the invoice prints for this service. */
  description: string;
  /** The rate, as typed; blank prices the service the way the client is priced. */
  fee: string;
  /**
   * The price option last chosen, so two schedules at the same figure still show
   * the one that was picked. Cleared on typing, when the figure is read back
   * against the options instead.
   */
  feeSource?: string;
  /** The service's own wording and standard fee, for the fall-backs. */
  serviceDescription?: string | null;
  defaultFee?: number | null;
  /** Shown when a bundle being edited names a service that has since been marked inactive. */
  inactive?: boolean;
  name?: string | null;
}

let draftSeq = 0;
const blankItem = (): ItemDraft => ({
  key: `item-${++draftSeq}`,
  service_id: "",
  quantity: "1",
  description: "",
  fee: "",
});

/** The price option for "no rate on the bundle": the client's own cascade decides. */
const CLIENT_RATE = "client";
const CATALOGUE_RATE = "catalogue";
const OWN_RATE = "own";
const scheduleRate = (feeScheduleId: string) => `schedule:${feeScheduleId}`;

/**
 * The bundle, and the services in it, in one dialog.
 *
 * Two writes on save — the bundle, then its whole item set — because the item
 * set is a `PUT` of everything rather than a diff. On a new bundle the first
 * write has to land before the second has a parent to hang off, so they are
 * sequential rather than concurrent, and a failure on the second leaves a bundle
 * with no services rather than a half-written set.
 */
function BundleDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: ServiceBundle | null;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);
  const [items, setItems] = useState<ItemDraft[]>([blankItem()]);

  /**
   * The bundle's stored items, fetched when an existing bundle is opened.
   *
   * Not taken from the list's `include=items`: the dialog may be opened from a
   * row the list has since refetched, and the editor is the one place that has
   * to be editing what is actually stored.
   */
  const { data: stored, isLoading: itemsLoading } = useQuery({
    queryKey: ["service_bundle_items", editing?.id],
    enabled: open && !!editing,
    queryFn: async () => {
      const { data, error } = await api.serviceBundles.items(editing!.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Each opening starts from the record, so a cancelled edit leaves nothing
  // behind for the next one.
  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? "");
    setName(editing?.name ?? "");
    setDescription(editing?.description ?? "");
    setNotes(editing?.notes ?? "");
    setActive(editing?.active ?? true);
    if (!editing) setItems([blankItem()]);
  }, [open, editing]);

  useEffect(() => {
    if (!open || !editing || !stored) return;
    setItems(
      stored.length > 0
        ? stored.map((item) => ({
            key: `item-${++draftSeq}`,
            id: item.id,
            service_id: item.service_id,
            quantity: String(item.quantity),
            description: item.description ?? "",
            fee: item.fee == null ? "" : String(item.fee),
            serviceDescription: item.service_invoice_description || item.service_name,
            defaultFee: item.service_default_fee,
            inactive: !item.service_active,
            name: item.service_name,
          }))
        : [blankItem()],
    );
  }, [open, editing, stored]);

  const patch = (key: string, change: Partial<ItemDraft>) =>
    setItems((ls) => ls.map((l) => (l.key === key ? { ...l, ...change } : l)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return notify.error("Give the bundle a code");
    if (!name.trim()) return notify.error("Give the bundle a name");

    // A line nobody picked a service on is one the operator started and
    // abandoned, not an error — only the filled ones are saved.
    const filled = items.filter((l) => l.service_id);
    if (filled.length === 0) return notify.error("Add at least one service");

    for (const [i, l] of filled.entries()) {
      if (!(Number(l.quantity) > 0)) {
        return notify.error(`Service ${i + 1}: quantity must be greater than zero`);
      }
      if (l.fee.trim() !== "" && !(Number(l.fee) >= 0)) {
        return notify.error(`Service ${i + 1}: the rate cannot be negative`);
      }
    }

    // The server refuses this too, but saying it here names the service rather
    // than only the rule.
    const seen = new Set<string>();
    for (const l of filled) {
      if (seen.has(l.service_id)) {
        return notify.error("The same service is in the bundle twice — set its quantity instead");
      }
      seen.add(l.service_id);
    }

    const values = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
      notes: notes.trim() || null,
      active,
    };

    setSaving(true);
    const saved = editing
      ? await api.serviceBundles.update(editing.id, values)
      : await api.serviceBundles.create(values);

    if (saved.error) {
      setSaving(false);
      return notify.error(saved.error);
    }

    const bundleId = editing?.id ?? saved.data?.id;
    if (!bundleId) {
      setSaving(false);
      return notify.error("The bundle saved but returned no id; reopen it to add its services");
    }

    const payload: ServiceBundleItemDraft[] = filled.map((l, index) => ({
      id: l.id,
      service_id: l.service_id,
      quantity: Number(l.quantity),
      description: l.description.trim() || null,
      fee: l.fee.trim() === "" ? null : Number(l.fee),
      sort_order: index,
    }));

    const { error } = await api.serviceBundles.replaceItems(bundleId, payload);
    setSaving(false);
    if (error) return notify.error(error);

    notify.success(
      `${values.name} saved with ${payload.length} service${payload.length === 1 ? "" : "s"}`,
    );
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[44rem] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit bundle" : "New bundle"}</DialogTitle>
          <DialogDescription>
            The services this bundle stands for, how many of each, what the invoice calls them and
            what they are sold at. Leave a rate on “Client's rate” to price that service the way the
            client is always priced.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_minmax(0,1fr)_9rem]">
              <div className="space-y-2">
                <Label required>Code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="INC-STD"
                />
              </div>
              <div className="space-y-2">
                <Label required>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Standard incorporation"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={active ? "active" : "inactive"}
                  onValueChange={(v) => setActive(v === "active")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Shown under the name in the pickers"
              />
            </div>

            <div className="space-y-3 rounded-xl border bg-muted/40 p-3">
              <div className="flex items-center justify-between px-1">
                <Label required>Services in this bundle</Label>
                <span className="text-xs text-muted-foreground">
                  {items.length} line{items.length === 1 ? "" : "s"}
                </span>
              </div>

              {itemsLoading ? (
                <p className="px-1 py-6 text-center text-sm text-muted-foreground">
                  Loading services…
                </p>
              ) : (
                <div className="space-y-3">
                  {items.map((l, i) => (
                    <div key={l.key} className="rounded-lg border bg-card p-3 shadow-sm">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          Service {i + 1}
                        </span>
                        {items.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setItems((ls) => ls.filter((x) => x.key !== l.key))}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_6rem]">
                        <div className="space-y-2">
                          <Label className="text-xs">Service</Label>
                          <ServiceSelect
                            value={l.service_id}
                            onChange={() => {}}
                            onPick={(option) =>
                              option.row && patch(l.key, pickedService(l, option.row))
                            }
                            // A bundle saved before this service was marked inactive still
                            // names it, so the picker has to be able to show it —
                            // otherwise the line reads as empty and saving would
                            // silently drop it.
                            filters={{ includeInactive: l.inactive }}
                            excludeIds={items
                              .filter((x) => x.key !== l.key && x.service_id)
                              .map((x) => x.service_id)}
                            contentClassName="w-80 p-0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs" required={!!l.service_id}>
                            Qty
                          </Label>
                          <Input
                            type="number"
                            step="any"
                            min="0"
                            value={l.quantity}
                            onChange={(e) => patch(l.key, { quantity: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="mt-3 space-y-2">
                        <Label className="text-xs">Invoice description</Label>
                        <Input
                          value={l.description}
                          onChange={(e) => patch(l.key, { description: e.target.value })}
                          placeholder={l.serviceDescription || "Printed on the invoice line"}
                          disabled={!l.service_id}
                        />
                      </div>
                      <BundleItemRate item={l} onChange={(change) => patch(l.key, change)} />
                      {l.inactive && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
                          <TriangleAlert className="h-3.5 w-3.5" />
                          {l.name ?? "This service"} is inactive — remove it, or pick a replacement.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed"
                onClick={() => setItems((ls) => [...ls, blankItem()])}
              >
                <Plus className="mr-2 h-4 w-4" /> Add service
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Internal notes</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Not printed anywhere"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save bundle" : "Create bundle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What picking a service does to a line: the service changes, and so do the
 * wording and rate that were only ever the previous service's.
 *
 * Wording the operator typed is kept; wording that was just the previous service's
 * own is replaced with the new one's, so the invoice line never names a service
 * the line no longer holds. The rate is always cleared — a figure picked from the
 * previous service's schedules is not a price for this one.
 */
function pickedService(line: ItemDraft, row: ServiceRow): Partial<ItemDraft> {
  const own = row.invoice_description || row.name;
  const typed = line.description.trim();
  const keep = typed !== "" && typed !== (line.serviceDescription ?? "").trim();
  return {
    service_id: row.id,
    description: keep ? line.description : own,
    serviceDescription: own,
    defaultFee: row.default_fee ?? null,
    fee: "",
    feeSource: undefined,
    inactive: false,
    name: null,
  };
}

/**
 * The rate a bundle sells one service at, with the published rates to pick from.
 *
 * The options are what every fee schedule charges for this service today —
 * Somasi Standard and each client group's — plus the service's standard fee, read from the
 * same endpoint the service page uses. Picking one copies its figure in; the
 * figure is what is saved, so the bundle's price holds still when a schedule is
 * repriced later. "Client's rate" saves no figure, and the line is priced the way
 * that client is always priced when the bundle is expanded.
 */
function BundleItemRate({
  item,
  onChange,
}: {
  item: ItemDraft;
  onChange: (change: Partial<ItemDraft>) => void;
}) {
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["service_fee_schedules", item.service_id],
    enabled: !!item.service_id,
    queryFn: () => listServiceSchedules(item.service_id),
  });

  const options: (SelectOption & { fee?: number })[] = [
    { value: CLIENT_RATE, label: "Client's rate", hint: "Priced per client when expanded" },
    ...schedules.map((schedule) => ({
      value: scheduleRate(schedule.fee_schedule_id),
      label: `${schedule.schedule_name} — ${money(schedule.fee)}`,
      hint: `${schedule.active ? "In force" : "Inactive schedule"} from ${schedule.effective_from}`,
      fee: schedule.fee,
    })),
    // The service's own standard fee, unless the Somasi standard schedule is
    // already listed — the two are one number, and offering it twice reads as a
    // choice between prices that do not differ.
    ...(item.defaultFee != null && !schedules.some((schedule) => schedule.is_standard)
      ? [
          {
            value: CATALOGUE_RATE,
            label: `Standard fee — ${money(item.defaultFee)}`,
            hint: "The service's Somasi standard fee",
            fee: item.defaultFee,
          },
        ]
      : []),
  ];

  // Which option the figure is, when it was typed rather than picked: the first
  // published rate it matches, or "own figure" when it matches none.
  const typed = item.fee.trim();
  const matched =
    typed === ""
      ? CLIENT_RATE
      : (options.find((o) => o.fee != null && Math.abs(o.fee - Number(typed)) < 0.005)?.value ??
        OWN_RATE);
  const value =
    item.feeSource && options.some((o) => o.value === item.feeSource) ? item.feeSource : matched;
  if (value === OWN_RATE) options.push({ value: OWN_RATE, label: "Own figure" });

  const choose = (choice: string) => {
    if (choice === OWN_RATE) return;
    const option = options.find((o) => o.value === choice);
    onChange({
      feeSource: choice,
      fee: option?.fee == null ? "" : String(option.fee),
    });
  };

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
      <div className="space-y-2">
        <Label className="text-xs">Price from</Label>
        <SearchableSelect
          value={value}
          onChange={choose}
          options={options}
          disabled={!item.service_id}
          placeholder={isLoading ? "Loading fee schedules…" : "Client's rate"}
          searchPlaceholder="Search fee schedules…"
          emptyText="No schedule matches that."
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Rate (USD)</Label>
        <Input
          type="number"
          step="any"
          min="0"
          value={item.fee}
          placeholder="Client's rate"
          disabled={!item.service_id}
          onChange={(e) => onChange({ fee: e.target.value, feeSource: undefined })}
        />
      </div>
    </div>
  );
}
